import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Collapse } from '@work4all/components/lib/components/collapse';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { DateTimeInputPicker } from '@work4all/components/lib/input/date-time-input-picker';

import { useUser } from '@work4all/data';

import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';
import { ProjectHoursBillingBy } from '@work4all/models/lib/Enums/ProjectHoursBillingBy.enum';
import { ProjectHoursBillingType } from '@work4all/models/lib/Enums/ProjectHoursBillingType.enum';

import {
  canAddDeliveryNote,
  canAddInvoice,
} from '@work4all/utils/lib/permissions';

import { ControllerPlus } from '../../../../../containers/mask-overlays/form-plus/controller-plus';
import { useFormContextPlus } from '../../../../../containers/mask-overlays/form-plus/use-form-context-plus';
import { ControlWrapper } from '../../../../../containers/mask-overlays/mask-overlay/components';
import { BzObjTypePickerField } from '../../../../../containers/mask-overlays/mask-overlay/views/service-contract-billing/components/components/bzObjType-picker/BzObjTypePickerField';
import { ProjectHoursBillingByPickerField } from '../../../../entity-picker/ProjectHoursBillingByPickerField';
import { ProjectHoursBillingTypePickerField } from '../../../../entity-picker/ProjectHoursBillingTypePickerField';
import { ProjectHourBillingOverlayFormValue } from '../types';

import { KEY_NAME_BY_BILLING_BY } from './constants';

const BILLING_BY_TYPES_WITH_RELATED_RECORDS = [
  ProjectHoursBillingBy.CUSTOMER,
  ProjectHoursBillingBy.ORDER_CONFIRMATION,
];

const BZ_OBJ_TYPE_PICKER_FIELD_DISABLED_KEYS = [
  BzObjType.UNKNOWN,
  BzObjType.KALKULATION,
  BzObjType.AUFTRAG,
  BzObjType.ANGEBOT,
  BzObjType.BESTELLUNG,
  BzObjType.BEDARFSANFORDERUNG,
  BzObjType.EINGANGSLIEFERSCHEIN,
];

type Props = {
  relatedRecords: (Customer | Contract)[];
};

export const ProjectHourBillingOverlayBillingType = ({
  relatedRecords,
}: Props) => {
  const { t } = useTranslation();
  const { control } = useFormContextPlus<ProjectHourBillingOverlayFormValue>();

  const user = useUser();
  const invoiceEnabled = canAddInvoice(user);
  const deliveryNoteEnabled = canAddDeliveryNote(user);

  const disabledChoices = [
    ...BZ_OBJ_TYPE_PICKER_FIELD_DISABLED_KEYS,
    !invoiceEnabled ? BzObjType.RECHNUNG : null,
    !deliveryNoteEnabled ? BzObjType.LIEFERSCHEIN : null,
  ].filter(Boolean);

  return (
    <Collapse title={t('COMMON.BILLING_TYPE')} defaultOpen>
      <ControlWrapper columns={2}>
        <ControllerPlus
          control={control}
          name="settings"
          render={({ field }) => {
            return (
              <BzObjTypePickerField
                {...field}
                value={field.value.bzObjType}
                onChange={(value) => {
                  field.onChange({
                    ...field.value,
                    bzObjType: value,
                  });
                }}
                label={t('COMMON.RECEIPT')}
                disabledKeys={disabledChoices}
              />
            );
          }}
        />

        <ControllerPlus
          control={control}
          name="settings"
          render={({ field }) => {
            return (
              <DateTimeInputPicker
                {...field}
                value={new Date(field.value.documentDate)}
                onChange={(e) => {
                  field.onChange({
                    ...field.value,
                    //eslint-disable-next-line
                    //@ts-ignore
                    documentDate: e.target.value,
                  });
                }}
                dateLabel={t('COMMON.DOCUMENT_DATE')}
                clearable={false}
                withTime={false}
              />
            );
          }}
        />

        <ControllerPlus
          control={control}
          name="settings"
          render={({ field }) => {
            return (
              <ProjectHoursBillingByPickerField
                {...field}
                value={field.value.billingBy}
                onChange={(value) => {
                  field.onChange({
                    ...field.value,
                    billingBy: value,
                    billingType:
                      value !== ProjectHoursBillingBy.ORDER_CONFIRMATION &&
                      field.value.billingType ===
                        ProjectHoursBillingType.GROUPED_BY_ORDER_CONFIRMATION_POSITION
                        ? ProjectHoursBillingType.LIST_ALL_SERVICES
                        : field.value.billingType,
                  });
                }}
                clearable={false}
              />
            );
          }}
        />

        <ControllerPlus
          control={control}
          name="settings"
          render={({ field }) => {
            return (
              <ProjectHoursBillingTypePickerField
                {...field}
                value={field.value.billingType}
                onChange={(value) => {
                  field.onChange({
                    ...field.value,
                    billingType: value,
                  });
                }}
                clearable={false}
              />
            );
          }}
        />
      </ControlWrapper>

      <InvoiceAllCheckbox relatedRecords={relatedRecords} />
    </Collapse>
  );
};

const InvoiceAllCheckbox = ({ relatedRecords }) => {
  const { t } = useTranslation();
  const { watch, setValue } =
    useFormContextPlus<ProjectHourBillingOverlayFormValue>();

  const billingBy = watch('settings.billingBy');
  const customerCodes = watch('settings.customerCodes');
  const orderConfirmationCodes = watch('settings.orderConfirmationCodes');

  const showRelatedRecords = useMemo(() => {
    if (
      billingBy === ProjectHoursBillingBy.CUSTOMER &&
      relatedRecords.length === 1 &&
      customerCodes?.[0] === relatedRecords?.[0]?.id
    ) {
      return false;
    }
    if (
      billingBy === ProjectHoursBillingBy.ORDER_CONFIRMATION &&
      relatedRecords.length === 1 &&
      orderConfirmationCodes?.[0] === relatedRecords?.[0]?.id
    ) {
      return false;
    }
    return true;
  }, [billingBy, customerCodes, orderConfirmationCodes, relatedRecords]);

  const relatedRecordsLength = relatedRecords.length;
  const skip =
    !BILLING_BY_TYPES_WITH_RELATED_RECORDS.includes(billingBy) ||
    relatedRecordsLength === 0;

  if (!showRelatedRecords) {
    return null;
  }

  if (skip) {
    return null;
  }

  const keyName = KEY_NAME_BY_BILLING_BY[billingBy];
  const settings = watch('settings');
  const selectedRelatedRecords = settings[keyName];
  const checked = selectedRelatedRecords.length === relatedRecordsLength;
  const translationKey =
    billingBy === ProjectHoursBillingBy.CUSTOMER
      ? 'COMMON.CUSTOMER'
      : 'COMMON.CONTRACT';

  return (
    <CheckboxRadioItem
      label={t('COMMON.INVOICE_ALL')}
      checked={checked}
      caption={`${relatedRecordsLength} ${t(translationKey, {
        count: relatedRecordsLength,
      })}`}
      onChange={() => {
        if (checked) {
          setValue(
            'settings',
            { ...settings, [keyName]: [] },
            { shouldDirty: true }
          );
          return;
        }

        setValue(
          'settings',
          {
            ...settings,
            [keyName]: relatedRecords.map((relatedRecord) => relatedRecord.id),
          },
          { shouldDirty: true }
        );
      }}
    />
  );
};
